/**
 * The app an export runs in (the console or Netxol Admin) and the page it runs on.
 *
 * App (set once by each entry page's main.tsx): whose name and company go on the file, where the export is recorded
 * (each app's own API and audit trail), where a Netxol Report is rendered (the console: the ISP's Core, in the ISP's
 * brand; Netxol Admin: the platform, in Netxol's brand) and which part of the product the current page belongs to.
 *
 * Page: KPI cards and charts register themselves here (KpiCard, TrendChart, RankBars, Donut, Funnel), so a table
 * export, a dashboard export or a print can put the page's own headline figures and charts into the report.
 */
import { useCallback, useRef } from 'react';
import type { ReportBlock, ReportDoc, ReportKpi } from '@/shared/lib/report';

export interface ExportLog { title: string; format: 'xlsx' | 'csv' | 'pdf'; rows: number; filters: [string, string][] }
export interface ExportContext {
  company: () => string;
  who: () => string;
  log: (entry: ExportLog) => Promise<unknown>;
  /** Render a Netxol Report and save the file; resolves with its report number. */
  render: (doc: ReportDoc, format: 'pdf' | 'docx' | 'xlsx') => Promise<string | null>;
  /** The product area of the current page, e.g. "CRM" or "Network · NMM" (the eyebrow of an exported report). */
  section: () => string;
}

let current: ExportContext | null = null;
export const setExportContext = (c: ExportContext) => { current = c; };
export const exportContext = (): ExportContext | null => current;

/** Save a rendered report response (blob + Content-Disposition) as a download; returns its report number. */
export function saveRenderedReport(r: { data: unknown; headers: Record<string, unknown> }, fallback: string): string | null {
  const cd = String(r.headers['content-disposition'] ?? '');
  const name = decodeURIComponent(cd.match(/filename\*=UTF-8''([^;]+)/)?.[1] ?? '') || cd.match(/filename="([^"]+)"/)?.[1] || fallback;
  const url = URL.createObjectURL(r.data as Blob);
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
  return (r.headers['x-report-no'] as string) ?? null;
}

/** A failed render answers with a JSON error inside a Blob: read it so the usual error text (apiError) can show it. */
export async function readBlobError(e: unknown): Promise<never> {
  const res = (e as { response?: { data?: unknown } })?.response;
  if (res && typeof Blob !== 'undefined' && res.data instanceof Blob) { try { res.data = JSON.parse(await res.data.text()); } catch { /* not JSON */ } }
  throw e;
}

/* ───────── what the page shows: KPI cards and charts ───────── */
export type ReportPart = { type: 'kpi'; kpi: ReportKpi } | { type: 'chart'; block: Extract<ReportBlock, { type: 'chart' }> };
const parts = new WeakMap<Element, () => ReportPart | null>();

/** Register a KPI card or a chart: returns a ref for its outer element. `get` is read only when a report is made. */
export function useReportPart(get: (el: HTMLElement) => ReportPart | null) {
  const fn = useRef(get); fn.current = get;
  return useCallback((el: HTMLElement | null) => {
    if (el) { el.setAttribute('data-report-part', ''); parts.set(el, () => fn.current(el)); }
  }, []);
}

/** The KPI cards and charts shown inside `root` (on screen, in page order), leaving out those in `skip` containers. */
export function reportPartsIn(root: Element, opts: { skip?: string } = {}): { el: Element; part: ReportPart }[] {
  const out: { el: Element; part: ReportPart }[] = [];
  for (const el of root.querySelectorAll('[data-report-part]')) {
    if (opts.skip && el.closest(opts.skip) && !root.closest(opts.skip)) continue;
    if (!el.getClientRects().length) continue;   // hidden (another tab, collapsed)
    try { const p = parts.get(el)?.(); if (p) out.push({ el, part: p }); } catch { /* a part that cannot describe itself is left out */ }
  }
  return out;
}

/** The scope a table or widget belongs to: its dialog / drawer, otherwise the page. */
export function pageRootOf(el: Element | null): Element {
  return el?.closest('[role=dialog]') ?? document.querySelector('main') ?? document.body;
}

/** The title shown above an element: its panel title, else its dashboard widget title. */
export function titleAbove(el: Element): string | undefined {
  const panel = el.closest('[data-panel]')?.querySelector('[data-panel-title]')?.textContent?.trim();
  if (panel) return panel;
  const w = el.closest('[data-wid]')?.querySelector('[data-widget-title]')?.textContent?.trim();
  return w || undefined;
}
